import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Surfaces a dismissible banner when a new service worker has downloaded
 * and is waiting to activate. The SW's install handler deliberately does
 * NOT call skipWaiting() — that way a new deploy doesn't yank running JS
 * out from under the user. Instead:
 *
 *  1. On mount we grab the current SW registration and inspect it:
 *     - If there's already a `waiting` worker, show the banner now.
 *     - Otherwise listen for `updatefound` and watch the `installing`
 *       worker transition to `installed`. That's our cue (but only if
 *       there's an existing controller — on the very first install of
 *       the PWA there's no prior JS to replace, so we stay silent).
 *  2. "Reload to update" posts SKIP_WAITING to the waiting worker, then
 *     listens for `controllerchange` and does a full page reload so the
 *     fresh hashed JS bundle is actually fetched.
 *  3. "Dismiss" hides for this tab's lifetime. We don't persist to
 *     localStorage — users who ignore the banner still get the new
 *     version on their next reload, which is the correct fallback.
 */

export function UpdateAvailableBanner() {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // Critical: only reload on `controllerchange` if WE asked the waiting
  // worker to skip — otherwise a brand-new SW calling `clients.claim()`
  // in its activate handler will fire controllerchange on every first
  // load and put the app into a reload loop that blocks login.
  const reloadArmed = useRef(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let reg: ServiceWorkerRegistration | null = null;

    const onUpdateFound = () => {
      if (!reg) return;
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        // A new worker reached `installed` and is now `waiting`. Only
        // surface the prompt when there's an existing controller — a
        // first-ever install isn't an "update".
        if (
          installing.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          setWaiting(installing);
        }
      });
    };

    const onControllerChange = () => {
      // Only honor controllerchange reloads when the user clicked
      // "Reload" (which arms the flag via handleReload). A naked
      // controllerchange from `clients.claim()` on first install must
      // NOT trigger a reload — that caused the infinite-refresh bug.
      if (!reloadArmed.current) return;
      reloadArmed.current = false;
      window.location.reload();
    };

    navigator.serviceWorker
      .getRegistration()
      .then((r) => {
        if (!r) return;
        reg = r;
        if (r.waiting && navigator.serviceWorker.controller) {
          setWaiting(r.waiting);
        }
        r.addEventListener("updatefound", onUpdateFound);
      })
      .catch(() => {
        /* SW unavailable — nothing to update */
      });

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange,
    );

    return () => {
      if (reg) reg.removeEventListener("updatefound", onUpdateFound);
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
    };
  }, []);

  if (!waiting || dismissed) return null;

  const handleReload = () => {
    // Arm the reload BEFORE posting SKIP_WAITING so the ensuing
    // controllerchange actually reloads. Without this flag the listener
    // treats every controllerchange as a no-op (see onControllerChange).
    reloadArmed.current = true;
    waiting.postMessage({ type: "SKIP_WAITING" });
  };

  return (
    <div
      role="region"
      aria-label="Update available"
      className="mb-4 flex items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3 shadow-sm"
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">
          Update available
        </p>
        <p className="text-xs text-text-muted">
          A new version of Lenavio is ready. Reload to get the latest
          features and fixes.
        </p>
      </div>
      <Button size="sm" onClick={handleReload}>
        Reload
      </Button>
      <button
        type="button"
        aria-label="Dismiss update notification"
        onClick={() => setDismissed(true)}
        className="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-background transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
